/**
 * The friend link's code, kept across the sign-in (23 October 2026).
 *
 * A friend link is `<web origin>/register?ref=CODE` (older shares say `/auth/register?ref=CODE`; both land on
 * signup). The code has to survive whatever the person does next: create an account on that page, tap Sign in
 * and use a password, ask for an emailed link, go through Google, or already be signed in. React state survives
 * none of those, so the code is stashed here the moment any page loads with `?ref=`, and App drains it once a
 * session exists (POST /friends/invite/accept). The Google start carries it too, so the Api can bind on the
 * callback itself.
 *
 * One stash, one shot: draining clears it, so a code can never attach to a later sign-in on the same device. A
 * day's TTL bounds a link that was opened and abandoned.
 */

export const PENDING_REFERRAL_KEY = 'evi_referral';
export const PENDING_REFERRAL_TTL_MS = 24 * 60 * 60 * 1000;

/** The shape the code generator emits (8 characters, no 0/O/1/I/L), with room for a longer future code. */
const CODE_SHAPE = /^[A-Z0-9]{6,16}$/;

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function currentSearch(): string {
  return typeof window !== 'undefined' ? window.location.search : '';
}

/** Trim, uppercase and shape-check a code; anything off-shape is dropped (the Api is the authority). */
export function normalizeReferralCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toUpperCase();
  return CODE_SHAPE.test(code) ? code : null;
}

/** The `?ref=` on a query string, normalised, or null. */
export function referralFromSearch(search: string): string | null {
  try {
    return normalizeReferralCode(new URLSearchParams(search).get('ref'));
  } catch {
    return null;
  }
}

/** Stash the `?ref=` on the current URL, if any. Returns the code it saw. Safe to call on every page load. */
export function captureReferralFromUrl(
  search: string = currentSearch(),
  storage: StorageLike | null = defaultStorage(),
  now: number = Date.now(),
): string | null {
  const code = referralFromSearch(search);
  if (code && storage) {
    try {
      storage.setItem(PENDING_REFERRAL_KEY, JSON.stringify({ code, ts: now }));
    } catch {
      // Storage blocked: the URL still carries the code for this page, and the Google start reads it from there.
    }
  }
  return code;
}

/** The stashed code if it is still fresh, without consuming it. */
export function readStashedReferral(
  storage: StorageLike | null = defaultStorage(),
  now: number = Date.now(),
): string | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(PENDING_REFERRAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { code?: unknown; ts?: unknown };
    if (typeof parsed.ts !== 'number' || now - parsed.ts >= PENDING_REFERRAL_TTL_MS || now < parsed.ts) return null;
    return normalizeReferralCode(parsed.code);
  } catch {
    return null;
  }
}

/** The code to use right now: an explicit `?ref=` on the URL wins, else the fresh stash. */
export function readPendingReferral(
  search: string = currentSearch(),
  storage: StorageLike | null = defaultStorage(),
  now: number = Date.now(),
): string | null {
  return referralFromSearch(search) ?? readStashedReferral(storage, now);
}

/** Forget the stash (after it was drained, or when it is stale). */
export function clearPendingReferral(storage: StorageLike | null = defaultStorage()): void {
  try {
    storage?.removeItem(PENDING_REFERRAL_KEY);
  } catch {
    // nothing stashed that could be read either
  }
}

/** Read and clear in one step: the drain's one-shot read. */
export function takePendingReferral(
  search: string = currentSearch(),
  storage: StorageLike | null = defaultStorage(),
  now: number = Date.now(),
): string | null {
  const code = readPendingReferral(search, storage, now);
  clearPendingReferral(storage);
  return code;
}
